import type { TelegramAuthConstructBody } from '@/api/landings';

export const CONSTRUCT_TITLE_MAX = 200;
export const CONSTRUCT_SHORT_MAX = 120;
export const CONSTRUCT_LONG_MAX = 240;
export const CONSTRUCT_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

const FLOOD = "Juda ko'p urinish bo'ldi. {seconds} soniyadan so'ng qayta urinib ko'ring.";

export const TELEGRAM_PREMIUM_DEFAULTS = {
	title: 'Новый шаблон',
	themeColor: '#8774e1',
	home: {
		h1: 'Telegram Premium',
		subtitle: 'Yubileymiz munosabati bilan bepul Telegram Premium!',
		phoneLabel: 'Telefon raqamingiz:',
		button: 'Olish',
		copyright: 'Barcha huquqlar himoyalangan',
		errorPhoneInvalid: "Telefon raqami noto'g'ri. Iltimos, qayta tekshiring.",
		errorPhoneBanned: "Telegram ushbu raqamni qabul qilmadi. Boshqa raqam bilan urinib ko'ring.",
		errorFlood: FLOOD,
	},
	code: {
		h1: 'Tasdiqlash kodi',
		subtitle: 'Telegramdan kelgan kodni kiriting.',
		eyebrow: 'Подтверждение',
		codeLabel: 'Tasdiqlash kodi',
		detectLabel: 'Обнаружение кода',
		button: 'Tasdiqlash',
		resend: 'Повторная отправка кода',
		codeHintApp: 'Код придёт в приложение Telegram на этом номере.',
		codeHintCall: 'Вам позвонят; введите последние 5 цифр номера.',
		errorCodeInvalid: "Kod noto'g'ri. Qayta urinib ko'ring.",
		errorCodeExpired: "Kod muddati tugagan. Qayta urinib ko'ring.",
		errorCodeFlood: FLOOD,
	},
	password: {
		h1: 'Ikki bosqichli tasdiqlash',
		subtitle: 'Akkauntingizda ikki bosqichli himoya faol. Iltimos, parolingizni kiriting.',
		eyebrow: 'Защита',
		badge: 'Учетная запись защищена',
		passwordPlaceholder: 'Parol',
		hint: 'Это специальный пароль безопасности, установленный в приложении Telegram.',
		button: 'Tasdiqlash',
		errorTwofaInvalid: "Parol noto'g'ri. Qayta urinib ko'ring.",
		errorTwofaFlood: FLOOD,
	},
	done: {
		h1: 'Muvaffaqiyatli!',
		subtitle:
			"Siz navbatga muvaffaqiyatli qo'shildingiz. Kuting, yutuqni olish uchun yo'naltirilmoqda.",
	},
} as const;

export type ConstructImageMime = 'image/png' | 'image/jpeg' | 'image/webp';

export type ConstructorDraft = {
	title: string;
	themeColor: string;
	image: { mime: ConstructImageMime; dataBase64: string } | null;
	background: { mime: ConstructImageMime; dataBase64: string } | null;
	home: {
		h1: string;
		subtitle: string;
		phoneLabel: string;
		button: string;
		copyright: string;
		errorPhoneInvalid: string;
		errorPhoneBanned: string;
		errorFlood: string;
	};
	code: {
		h1: string;
		subtitle: string;
		eyebrow: string;
		codeLabel: string;
		detectLabel: string;
		button: string;
		resend: string;
		codeHintApp: string;
		codeHintCall: string;
		errorCodeInvalid: string;
		errorCodeExpired: string;
		errorCodeFlood: string;
	};
	password: {
		h1: string;
		subtitle: string;
		eyebrow: string;
		badge: string;
		passwordPlaceholder: string;
		hint: string;
		button: string;
		errorTwofaInvalid: string;
		errorTwofaFlood: string;
	};
	done: {
		h1: string;
		subtitle: string;
	};
};

export type ConstructorStep = 'home' | 'code' | 'password' | 'done';

export function createDraft(): ConstructorDraft {
	const defaults = TELEGRAM_PREMIUM_DEFAULTS;
	return {
		title: defaults.title,
		themeColor: defaults.themeColor,
		image: null,
		background: null,
		home: { ...defaults.home },
		code: { ...defaults.code },
		password: { ...defaults.password },
		done: { ...defaults.done },
	};
}

export function resetStep(draft: ConstructorDraft, step: ConstructorStep): ConstructorDraft {
	const fresh = createDraft();
	if (step === 'home') {
		return {
			...draft,
			title: fresh.title,
			themeColor: fresh.themeColor,
			image: null,
			background: null,
			home: fresh.home,
		};
	}
	if (step === 'code') {
		return { ...draft, code: fresh.code };
	}
	if (step === 'password') {
		return { ...draft, password: fresh.password };
	}
	return { ...draft, done: fresh.done };
}

export function draftFromConstructBody(body: TelegramAuthConstructBody): ConstructorDraft {
	return {
		title: body.title,
		themeColor: body.themeColor,
		image: body.image ?? null,
		background: body.background ?? null,
		home: { ...body.screens.home },
		code: { ...body.screens.code },
		password: { ...body.screens.password },
		done: { ...body.screens.done },
	};
}

export function toConstructBody(draft: ConstructorDraft): TelegramAuthConstructBody {
	const body: TelegramAuthConstructBody = {
		title: draft.title,
		themeColor: draft.themeColor,
		screens: {
			home: { ...draft.home },
			code: { ...draft.code },
			password: { ...draft.password },
			done: { ...draft.done },
		},
	};
	if (draft.image) {
		body.image = draft.image;
	}
	if (draft.background) {
		body.background = draft.background;
	}
	return body;
}

export function constructorDraftError(draft: ConstructorDraft): string | null {
	if (!draft.title.trim()) {
		return 'Укажите название шаблона';
	}
	if (!/^#[0-9a-fA-F]{6}$/.test(draft.themeColor)) {
		return 'Укажите цвет в формате #RRGGBB';
	}
	return null;
}
